/** Shared helpers for skill editing (used by AdminSkillDetails, NewSkillDialog, SkillsTable). */

import type { ISkill } from "@/api/skills";

// ── Status display ──────────────────────────────────────────────────

export const SKILL_STATUS_MAP: Record<
    ISkill["status"],
    { label: string; variant: "default" | "secondary" }
> = {
    active: { label: "Published", variant: "default" },
    inactive: { label: "Draft", variant: "secondary" },
};

// ── File helpers ────────────────────────────────────────────────────

/** Local working-copy of a file (may be new or from server). */
export interface LocalFile {
    /** Server ID, or undefined for unsaved files. */
    id?: number;
    name: string;
    content: string;
    /** Populated by validateFileNames when a duplicate is detected. */
    nameError?: string;
    /** Populated by validateFileNames when content is empty. */
    contentError?: string;
}

/**
 * Mark duplicate file names with nameError. Returns a new array with
 * nameError set on duplicates and cleared on unique entries.
 */
export function validateFileNames(files: LocalFile[]): LocalFile[] {
    const names = files.map((f) => f.name.trim().toLowerCase());
    return files.map((f, i) => ({
        ...f,
        nameError:
            names[i] && names.indexOf(names[i]) !== i
                ? "Duplicate name"
                : undefined,
        contentError:
            f.name.trim() && !f.content.trim()
                ? "Add content to this file"
                : undefined,
    }));
}
